import React from 'react';

import { AccessTime, Circle, LocationOn, People, VideoCall } from '@mui/icons-material';
import { Card, CardActions, CardContent, Typography, Box, Stack } from '@mui/material';

import { Event } from '../hooks/useNextEvents';
import { LiveBadgePill } from 'components/Badges/LiveBadgePill';
import Button from 'components/Button';
import { useIsMobile } from 'utils/mediaQueries';

interface OngoingEventProps {
  event: Event;
  onDetails: () => void;
  onAccess: () => void;
}

const OngoingEvent = ({ event, onDetails, onAccess }: OngoingEventProps) => {
  const isMobile = useIsMobile();

  return (
    <Card
      sx={{
        width: '100%',
        borderRadius: 2,
        backgroundColor: 'background.primary',
      }}
    >
      <Box padding={isMobile ? 1.5 : 2}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems={isMobile ? 'flex-start' : 'center'}
          flexDirection="row"
          gap={isMobile ? 1 : 0}
        >
          <Typography
            variant={isMobile ? 'subtitle1' : 'h6'}
            fontWeight="bold"
            sx={{
              fontSize: isMobile ? '1rem' : undefined,
              lineHeight: isMobile ? 1.2 : undefined,
            }}
          >
            {event.title}
          </Typography>
          {event.isOnline && (
            <>
              <LiveBadgePill>AO VIVO</LiveBadgePill>
            </>
          )}
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          {event.isOnline && (
            <Circle
              color="error"
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: 'error',
                animation: 'pulse 1.5s infinite',
                color: 'error',
              }}
            />
          )}
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {event.date} às {event.time}
          </Typography>
        </Box>
      </Box>

      <CardContent sx={{ padding: isMobile ? 1.5 : 2 }}>
        <Stack spacing={isMobile ? 0.75 : 1}>
          <Box display="flex" alignItems="center" gap={1}>
            <AccessTime fontSize="small" color="primary" />
            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.875rem' : undefined }}>
              Duração: {event.duration}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <LocationOn fontSize="small" color="primary" />
            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.875rem' : undefined }}>
              {event.location}
            </Typography>
          </Box>

          {event.totalParticipants > 0 && (
            <Box display="flex" alignItems="center" gap={1}>
              <People fontSize="small" color="primary" />
              <Typography variant="body2" sx={{ fontSize: isMobile ? '0.875rem' : undefined }}>
                {event.totalParticipants} participante{event.totalParticipants > 1 ? 's' : ''}
              </Typography>
            </Box>
          )}

          {event.coordinator && (
            <Box>
              <Typography variant="body2" sx={{ fontSize: isMobile ? '0.875rem' : undefined }}>
                <strong>Coordenador:</strong> {event.coordinator.firstName}{' '}
                {event.coordinator.lastName}
              </Typography>
            </Box>
          )}

          {event.presenters.length > 0 && (
            <Box>
              <Typography variant="body2" sx={{ fontSize: isMobile ? '0.875rem' : undefined }}>
                <strong>Palestrante{event.presenters.length > 1 ? 's' : ''}:</strong>{' '}
                {event.presenters.map(p => `${p.firstName} ${p.lastName}`).join(', ')}
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>

      <CardActions
        sx={{
          display: 'flex',
          width: '100%',
          padding: isMobile ? 1.5 : 2,
          flexDirection: isMobile ? 'column' : 'row',
          gap: 1,
        }}
      >
        <Button variant="outline" onClick={onDetails} fullWidth>
          Ver mais detalhes
        </Button>
        <Button
          variant="primary"
          onClick={onAccess}
          fullWidth
          icon={event.isOnline ? <VideoCall /> : <LocationOn />}
        >
          {event.isOnline ? 'Acessar sala virtual' : 'Ver localização'}
        </Button>
      </CardActions>
    </Card>
  );
};

export default OngoingEvent;
